import { createRouter, createWebHistory } from "vue-router";

import AdminLayout from "@/layouts/AdminLayout.vue";
import MemberLayout from "@/layouts/MemberLayout.vue";

import DashboardView from "@/views/admin/DashboardView.vue";
import MembersView from "@/views/admin/MembersView.vue";
import AdminBulkContributions from "@/views/admin/AdminBulkContributions.vue";

import { getInvestmentSettings } from "@/services/investmentService";

const routes = [
  /*
  |--------------------------------------------------------------------------
  | Guest Routes
  |--------------------------------------------------------------------------
  */

  {
    path: "/",
    name: "home",
    component: () => import("@/views/HomeView.vue"),
    meta: {
      guest: true,
    },
  },

  {
    path: "/login/sms",
    name: "login-sms",
    component: () => import("@/views/LoginViewSMS.vue"),
    meta: {
      guest: true,
    },
  },

  {
    path: "/login",
    name: "login",
    component: () => import("@/views/LoginView.vue"),
    meta: {
      guest: true,
    },
  },

  /*
  |--------------------------------------------------------------------------
  | System Owner Routes
  |--------------------------------------------------------------------------
  */

  {
    path: "/owner",
    name: "SystemOwnerDashboard",
    component: () => import("@/views/owner/SystemOwnerDashboardView.vue"),
    meta: {
      requiresAuth: true,
      requiresSystemOwner: true,
      skipGroupCheck: true,
    },
  },

  {
    path: "/owner/groups",
    name: "OwnerGroups",
    component: () => import("@/views/owner/OwnerGroupsView.vue"),
    meta: {
      requiresAuth: true,
      requiresSystemOwner: true,
      skipGroupCheck: true,
    },
  },

  /*
  |--------------------------------------------------------------------------
  | Group Selection
  |--------------------------------------------------------------------------
  */

  {
    path: "/select-group",
    name: "SelectGroup",
    component: () => import("@/views/SelectGroupView.vue"),
    meta: {
      requiresAuth: true,
      skipGroupCheck: true,
    },
  },

  /*
  |--------------------------------------------------------------------------
  | Group Management
  |--------------------------------------------------------------------------
  */

  {
    path: "/groups",
    name: "GroupManagement",
    component: () => import("@/views/GroupManagementView.vue"),
    meta: {
      requiresAuth: true,
      requiresGroup: true,
      allowedGroupRoles: ["owner", "admin"],
    },
  },

  /*
  |--------------------------------------------------------------------------
  | Member Area
  |--------------------------------------------------------------------------
  */

  {
    path: "/me",
    component: MemberLayout,
    meta: {
      requiresAuth: true,
      requiresGroup: true,
    },

    children: [
      {
        path: "",
        name: "member-dashboard",
        component: () => import("@/views/me/MemberDashboardView.vue"),
      },

      {
        path: "statement",
        name: "my-statements",
        component: () => import("@/views/me/MyStatementView.vue"),
      },

      {
        path: "contributions",
        name: "my-contributions",
        component: () => import("@/views/me/MyContributionsView.vue"),
      },

      {
        path: "loans",
        name: "my-loans",
        component: () => import("@/views/me/MyLoans.vue"),
      },

      {
        path: "my-loan-applications",
        name: "my-loan-applications",
        component: () => import("@/views/me/MyLoanApplicationsView.vue"),
      },

      {
        path: "withdrawals",
        name: "my-withdrawals",
        component: () => import("@/views/me/MyWithdrawalsView.vue"),
      },

      {
        path: "penalties",
        name: "my-penalties",
        component: () => import("@/views/me/MyPenaltiesView.vue"),
      },
    ],
  },

  /*
  |--------------------------------------------------------------------------
  | Admin Area
  |--------------------------------------------------------------------------
  */

  {
    path: "/admin",
    component: AdminLayout,
    meta: {
      requiresAuth: true,
      requiresGroup: true,
      allowedGroupRoles: ["owner", "admin", "treasurer"],
    },

    children: [
      /*
      |--------------------------------------------------------------------------
      | Dashboard
      |--------------------------------------------------------------------------
      */

      {
        path: "",
        name: "admin-dashboard",
        component: DashboardView,
      },

      /*
      |--------------------------------------------------------------------------
      | Members
      |--------------------------------------------------------------------------
      */

      {
        path: "members",
        name: "admin-members",
        component: MembersView,
      },

      {
        path: "members/:id/financial-statement",
        name: "MemberFinancialStatement",
        component: () =>
          import("@/views/admin/MemberFinancialStatementView.vue"),
      },

      /*
      |--------------------------------------------------------------------------
      | Contributions
      |--------------------------------------------------------------------------
      */

      {
        path: "contributions",
        name: "admin-contributions",
        component: () => import("@/views/admin/AdminContributionView.vue"),
      },

      {
        path: "contributions/bulk",
        name: "admin-contributions-bulk",
        component: AdminBulkContributions,
      },

      /*
      |--------------------------------------------------------------------------
      | Commitments
      |--------------------------------------------------------------------------
      */

      {
        path: "commitments",
        name: "admin-commitments",
        component: () => import("@/views/admin/AdminCommitmentView.vue"),
      },

      /*
      |--------------------------------------------------------------------------
      | Loans
      |--------------------------------------------------------------------------
      */

      {
        path: "loans",
        name: "admin-loans",
        component: () => import("@/views/admin/AdminLoansView.vue"),
      },

      {
        path: "loans/import",
        name: "AdminLoanBulkImport",
        component: () => import("@/views/admin/AdminLoanBulkImport.vue"),
      },

      {
        path: "loans/repayment-sheet",
        name: "LoanRepaymentSheet",
        component: () => import("@/views/admin/LoanRepaymentSheetView.vue"),
      },

      /*
      |--------------------------------------------------------------------------
      | Loan Applications
      |--------------------------------------------------------------------------
      */

      {
        path: "loan-applications-review",
        name: "loan-applications-review",
        component: () => import("@/views/admin/LoanApplicationsReviewView.vue"),
      },

      /*
      |--------------------------------------------------------------------------
      | Withdrawals
      |--------------------------------------------------------------------------
      */

      {
        path: "withdrawals",
        name: "withdrawal-review",
        component: () => import("@/views/admin/WithdrawalReviewView.vue"),
      },

      /*
      |--------------------------------------------------------------------------
      | Penalties
      |--------------------------------------------------------------------------
      */

      {
        path: "penalties",
        name: "admin-penalties",
        component: () => import("@/views/admin/AdminPenalties.vue"),
      },

      /*
      |--------------------------------------------------------------------------
      | Expenses
      |--------------------------------------------------------------------------
      */

      {
        path: "expenses",
        name: "admin-expenses",
        component: () => import("@/views/expenses/ExpensesView.vue"),
      },

      /*
      |--------------------------------------------------------------------------
      | Investments
      |--------------------------------------------------------------------------
      */

      {
        path: "investments",
        name: "admin-investments",
        component: () => import("@/views/admin/InvestmentManagementView.vue"),
        meta: {
          requiresInvestmentEnabled: true,
        },
      },
      {
        path: "/no-group",
        name: "no-group",
        component: () => import("@/views/NoGroupView.vue"),
        meta: {
          requiresAuth: true,
        },
      },

      /*
      |--------------------------------------------------------------------------
      | Profit Cycle
      |--------------------------------------------------------------------------
      */

      {
        path: "profit-cycle",
        name: "admin-profit-cycle",
        component: () => import("@/views/admin/ProfitCyclesAdmin.vue"),
      },

      /*
      |--------------------------------------------------------------------------
      | Statements
      |--------------------------------------------------------------------------
      */

      {
        path: "statement",
        name: "admin-statement",
        component: () => import("@/views/admin/AdminStatementView.vue"),
      },

      /*
      |--------------------------------------------------------------------------
      | Reports
      |--------------------------------------------------------------------------
      */

      {
        path: "reports/monthly-matrix",
        name: "MonthlyMatrixReport",
        component: () =>
          import("@/views/admin/reports/MonthlyMatrixReportView.vue"),
      },

      /*
      |--------------------------------------------------------------------------
      | Reconciliation
      |--------------------------------------------------------------------------
      */

      {
        path: "reconciliation",
        name: "Reconciliation",
        component: () => import("@/views/admin/ReconciliationView.vue"),
      },

      /*
      |--------------------------------------------------------------------------
      | Financial Year Settings
      |--------------------------------------------------------------------------
      */

      {
        path: "settings/financial-years",
        name: "admin-settings-financial-years",
        component: () => import("@/views/admin/SettingsFinancialYearRules.vue"),
      },

      /*
      |--------------------------------------------------------------------------
      | Group Rules
      |--------------------------------------------------------------------------
      */

      {
        path: "settings/rules",
        name: "GroupRulesSettings",
        component: () =>
          import("@/views/admin/settings/GroupRulesSettingsView.vue"),
        meta: {
          requiresAuth: true,
          requiresGroup: true,
          allowedGroupRoles: ["admin"],
        },
      },

      /*
      |--------------------------------------------------------------------------
      | Financial Year Closures
      |--------------------------------------------------------------------------
      */

      {
        path: "financial-year-closures",
        name: "FinancialYearClosures",
        component: () => import("@/views/admin/FinancialYearClosuresView.vue"),
        meta: {
          requiresAuth: true,
          requiresGroup: true,
          allowedGroupRoles: ["admin", "treasurer"],
        },
      },
    ],
  },

  /*
  |--------------------------------------------------------------------------
  | Unknown Route
  |--------------------------------------------------------------------------
  */

  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

/*
|--------------------------------------------------------------------------
| Investment Settings Cache
|--------------------------------------------------------------------------
|
| Prevents the router from requesting the group rules every time the user
| opens the investment page.
|
*/

let investmentSettingsCache = {
  groupId: null,
  enabled: null,
};

/*
|--------------------------------------------------------------------------
| Authentication Helpers
|--------------------------------------------------------------------------
*/

function isAuthed() {
  return Boolean(localStorage.getItem("token"));
}

function getUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

function getActiveGroupId() {
  return localStorage.getItem("active_group_id");
}

function getActiveGroupRole() {
  return localStorage.getItem("active_group_role") || "member";
}

function isSystemOwner(user) {
  return user?.role === "system_owner";
}

/*
|--------------------------------------------------------------------------
| Investment Rule Check
|--------------------------------------------------------------------------
*/

async function investmentIsEnabled() {
  const groupId = getActiveGroupId();

  if (!groupId) {
    return false;
  }

  const isSameGroup =
    String(investmentSettingsCache.groupId) === String(groupId);

  if (isSameGroup && investmentSettingsCache.enabled !== null) {
    return investmentSettingsCache.enabled;
  }

  const settings = await getInvestmentSettings();

  const enabled = Boolean(settings.enabled);

  investmentSettingsCache = {
    groupId,
    enabled,
  };

  return enabled;
}

/*
|--------------------------------------------------------------------------
| Navigation Guard
|--------------------------------------------------------------------------
*/

router.beforeEach(async (to) => {
  const authed = isAuthed();
  const user = getUser();

  const activeGroupId = getActiveGroupId();

  const activeGroupRole = getActiveGroupRole();

  /*
  |--------------------------------------------------------------------------
  | Logged-in User Visiting Guest Route
  |--------------------------------------------------------------------------
  */

  if (to.meta.guest && authed) {
    if (isSystemOwner(user)) {
      return {
        path: "/owner",
      };
    }

    if (!activeGroupId) {
      return {
        path: "/select-group",
      };
    }

    const adminRoles = ["owner", "admin", "treasurer"];

    return adminRoles.includes(activeGroupRole)
      ? {
          path: "/admin",
        }
      : {
          path: "/me",
        };
  }

  /*
  |--------------------------------------------------------------------------
  | Authentication Check
  |--------------------------------------------------------------------------
  */

  if (to.meta.requiresAuth && !authed) {
    return {
      path: "/login/sms",
    };
  }

  /*
  |--------------------------------------------------------------------------
  | System Owner Protection
  |--------------------------------------------------------------------------
  */

  if (to.meta.requiresSystemOwner && !isSystemOwner(user)) {
    return {
      path: "/",
    };
  }

  /*
  |--------------------------------------------------------------------------
  | System Owner Without Selected Group
  |--------------------------------------------------------------------------
  */

  if (isSystemOwner(user) && to.meta.requiresGroup && !activeGroupId) {
    return {
      path: "/owner",
    };
  }

  /*
  |--------------------------------------------------------------------------
  | Active Group Check
  |--------------------------------------------------------------------------
  */

  if (to.meta.requiresGroup && !to.meta.skipGroupCheck && !activeGroupId) {
    return {
      path: "/select-group",
    };
  }

  /*
  |--------------------------------------------------------------------------
  | Group Role Check
  |--------------------------------------------------------------------------
  */

  if (
    to.meta.allowedGroupRoles &&
    !to.meta.allowedGroupRoles.includes(activeGroupRole)
  ) {
    return {
      path: "/me",
    };
  }

  /*
  |--------------------------------------------------------------------------
  | Investment Module Check
  |--------------------------------------------------------------------------
  */

  if (to.meta.requiresInvestmentEnabled) {
    try {
      const enabled = await investmentIsEnabled();

      if (!enabled) {
        return {
          path: "/admin",
          query: {
            module: "investments-disabled",
          },
        };
      }
    } catch (error) {
      console.error("Failed to check investment settings:", error);

      return {
        path: "/admin",
        query: {
          module: "investments-unavailable",
        },
      };
    }
  }

  return true;
});

/*
|--------------------------------------------------------------------------
| Clear Investment Cache
|--------------------------------------------------------------------------
|
| Call this after switching groups or updating group investment rules.
|
*/

export function clearInvestmentSettingsCache() {
  investmentSettingsCache = {
    groupId: null,
    enabled: null,
  };
}

export default router;
